/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useEffect } from 'react';
import styled from 'styled-components';
import { toNumber } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { sendSentryError } from '../../utils/sentry';
import { useAlertModal } from '../../hooks';
import { ExchangeCode, useDisableSubscriptionMutation } from '../../apollo';
import { getDownMedia } from '../../styles';
import { useAlert, useModals } from '../../providers';
import { Button as DefButton } from '../buttons';
import { Text as DefText } from '../texts';

const DisableSubscriptionModal = () => {
  const { t } = useTranslation();
  const { setAlert } = useAlert();
  const { data, dispatch: dispatchModal } = useModals<'disableSubscription'>();
  const { dispatch: dispatchAlertModal } = useAlertModal();

  useEffect(() => {
    dispatchModal({
      type: 'setWrapperProps',
      payload: {
        modalName: 'disable-subscription',
        titleText: t('DISABLE_SUBSCRIPTION_MODAL__title'),
      },
    });
  }, [t, dispatchModal]);

  const [disableSubscription, { loading: disablingSubscription }] =
    useDisableSubscriptionMutation({
      variables: {
        exchangeId: toNumber(data?.exchangeID),
      },
      onCompleted: () => {
        dispatchModal({ type: 'closeModal' });
        dispatchAlertModal(['setModalContent', 'subscriptionDisabledSuccess']);
      },
      onError: (error) => {
        setAlert({
          type: 'error',
          message: t('DISABLE_SUBSCRIPTION_MODAL__errorText'),
        });
        sendSentryError(error);
      },
    });

  return (
    <>
      <Text>{t('DISABLE_SUBSCRIPTION_MODAL__desc')}</Text>
      <Subtitle>{t('DISABLE_SUBSCRIPTION_MODAL__subTitle')}</Subtitle>
      <Text>{t('DISABLE_SUBSCRIPTION_MODAL__desc2')}</Text>
      <Button
        loading={disablingSubscription}
        onClick={() => {
          disableSubscription();
        }}
      >
        {t('DISABLE_SUBSCRIPTION_MODAL__disableSubscriptionButtonText')}
      </Button>
    </>
  );
};

export type DisableSubscriptionModalData = {
  exchangeID: string;
  exchangeCode: ExchangeCode;
};

const Subtitle = styled(DefText).attrs({
  component: 'h3',
  variant: 'subtitle2',
})``;

const Text = styled(DefText)`
  margin-bottom: 30px;
  white-space: pre-line;

  ${getDownMedia('sm')} {
    margin-right: 0;
  }
`;

const Button = styled(DefButton)`
  width: 100%;
`;

export { DisableSubscriptionModal };
